/**
 * PRTimelineDialog — shows a chronological list of lifecycle events for one
 * PR (raise → self-review → L1 → L2 → PO). Triggered by the "History" button
 * in PRDetailDrawer. Kept intentionally simple: no email / WhatsApp delivery
 * data, no analytics. Just when + who + what.
 *
 * Data source: GET /api/prs/{voc_no}/timeline (see server.py). Backend derives
 * events from PoRequest header timestamps — no separate audit table needed.
 */
import { useEffect, useState } from "react";
import { prs } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

// One neutral date formatter for the whole timeline. Handles both
// date-only strings ("2026-08-01") and full datetimes ("2026-08-03T07:11:20").
// Date-only rows show just the date; datetimes show date + HH:mm.
function fmt(iso) {
  if (!iso) return "";
  // Backend returns naive strings; wrap in Date to let the browser format
  // in the user's locale. Missing tz means it's rendered as local time — which
  // matches how the DB stores it (SQL Server local time).
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const opts = dateOnly
    ? { day: "2-digit", month: "short", year: "numeric" }
    : { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
  return d.toLocaleString(undefined, opts);
}

export default function PRTimelineDialog({ vocNo, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);

  // Fetch fresh timeline every time the dialog opens — approvals happen
  // asynchronously and users would be confused by stale data.
  useEffect(() => {
    if (!open || !vocNo) return;
    setLoading(true);
    prs.timeline(vocNo)
      .then(({ data }) => setEvents(data?.events || []))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load timeline");
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [open, vocNo]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="pr-timeline-dialog">
        <DialogHeader>
          <DialogTitle>PR #{vocNo} — Timeline</DialogTitle>
          <DialogDescription>
            Every lifecycle step recorded on this PR, in order.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading history…
          </div>
        ) : events.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No history recorded for this PR yet.
          </div>
        ) : (
          <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-700 ml-2 pl-6 space-y-4"
              data-testid="pr-timeline-list">
            {events.map((ev, i) => {
              const isLast = i === events.length - 1;
              // "Reject" steps get a red dot, everything else green. Simple
              // heuristic — the backend already labels reject events distinctly
              // ("Requestor self-rejected", "L2 rejected").
              const isReject = /reject/i.test(ev.step || "");
              const IconEl = isLast ? Circle : CheckCircle2;
              return (
                <li key={i} className="relative"
                    data-testid={`pr-timeline-event-${i}`}>
                  <span className={
                    "absolute -left-[34px] top-0.5 rounded-full p-0.5 bg-white dark:bg-neutral-900 " +
                    (isReject ? "text-red-600" : isLast ? "text-blue-600" : "text-emerald-600")
                  }>
                    <IconEl className="h-4 w-4" />
                  </span>
                  <div className="text-sm font-medium leading-tight">
                    {ev.step}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmt(ev.ts)}
                    {ev.by_name && (
                      <>
                        {" · "}
                        <span className="text-neutral-700 dark:text-neutral-300">
                          {ev.by_name}
                        </span>
                        {ev.by_emp_no && !ev.by_name.includes(ev.by_emp_no) && (
                          <span className="text-neutral-400"> ({ev.by_emp_no})</span>
                        )}
                      </>
                    )}
                    {!ev.by_name && ev.by_emp_no && (
                      <> · <span>{ev.by_emp_no}</span></>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
